/* eslint react-hooks/exhaustive-deps: "warn" */

import { type ReactNode } from 'react';

import cn from 'classnames';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import type { TitleBlockSize } from 'ui/atoms/TitleBlock/TitleBlock.types';
import { type BridgeSize } from 'ui/molecules/SectionBridge/SectionBridge.types';

import styles from './Section.module.scss';

type Props = {
  id?: string;
  title?: ReactNode;
  titleSize?: TitleBlockSize;
  description?: ReactNode;
  extra?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  bottomContent?: ReactNode;
  layout?: 'centered' | 'split';
  splitAlign?: 'top' | 'center';
  splitReverse?: boolean;
  splitSticky?: boolean;
  splitWideLeft?: boolean;
  theme?: 'light' | 'dark';
  fullWidth?: boolean;
  background?: string;
  backgroundImage?: string;
  bridgeTop?: BridgeSize;
  bridgeBottom?: BridgeSize;
  className?: string;
  introBlockClassName?: string;
  align?: 'center' | 'left';
};

export const Section = ({
  id,
  title,
  titleSize,
  description,
  extra,
  actions,
  children,
  bottomContent,
  layout = 'centered',
  splitAlign = 'center',
  splitReverse = false,
  splitSticky = false,
  splitWideLeft = false,
  theme = 'light',
  fullWidth = false,
  background,
  backgroundImage,
  bridgeTop,
  bridgeBottom,
  className,
  introBlockClassName,
  align,
}: Props) => {
  const hasIntro = title || description || extra;
  const isSplit = layout === 'split';

  const inlineStyle = background
    ? { background }
    : backgroundImage
      ? { backgroundImage: `url(${backgroundImage})` }
      : undefined;

  return (
    <section
      id={id}
      className={cn(
        styles.section,
        styles[`theme_${theme}`],
        bridgeTop && styles[`bridgeTop_${bridgeTop}`],
        bridgeBottom && styles[`bridgeBottom_${bridgeBottom}`],
        className,
      )}
      style={inlineStyle}
    >
      <div className={styles.container}>
        {isSplit ? (
          <div
            className={cn(
              styles.splitLayout,
              styles[`splitLayout_${splitAlign}`],
              splitReverse && styles.splitReverse,
              splitWideLeft && styles.splitWideLeft,
            )}
          >
            <div className={cn(styles.splitLeft, splitSticky && styles.splitLeftSticky)}>
              {hasIntro && (
                <TitleBlock
                  align="left"
                  theme={theme}
                  size={titleSize}
                  title={title}
                  description={description}
                  extra={extra}
                  className={cn(styles.introBlock, introBlockClassName)}
                />
              )}

              {actions && <div className={styles.actions}>{actions}</div>}
            </div>

            {children && <div className={styles.splitRight}>{children}</div>}
          </div>
        ) : (
          <>
            {hasIntro && (
              <TitleBlock
                align={align}
                theme={theme}
                size={titleSize}
                fullWidth={fullWidth}
                title={title}
                description={description}
                extra={extra}
                className={introBlockClassName}
              />
            )}

            {children}

            {actions && <div className={styles.centeredActions}>{actions}</div>}
          </>
        )}
      </div>

      {bottomContent && <div className={styles.bottomContent}>{bottomContent}</div>}
    </section>
  );
};
